/**
 * Local-first mutations for FORUMS, ANNOUNCEMENTS and NOTIFICATIONS (F3-C).
 *
 * They write to PowerSync → write-path (`db/sync.rs`):
 *   • forum: course author/editor authz.
 *   • forum_thread/forum_post: cohort participant (or moderator); the
 *     server FORCES author_id, denormalizes forum_id/cohort_id/organization_id
 *     and only a moderator sets pinned/locked/hidden_*.
 *   • announcement: org_admin/campus_admin/teacher depending on scope; the
 *     notification fan-out is done by the server.
 *   • notification: the client ONLY marks its own read_at.
 * badge_award is server-only (no client mutation).
 */

import {
  and,
  announcement,
  course_module,
  eq,
  forum,
  forum_post,
  forum_thread,
  isNull,
  notification,
  type PowerSyncDrizzleDB,
} from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateId } from "@fleet/utils";
import {
  patchRowVerified,
  type VerifiedWriteOptions,
  withScope,
} from "../../powersync/verified-write";
import { addCourseModuleMutation } from "../modules/mutations";

/** Deterministic forum id per module (upsert idempotency). */
export function forumIdForModule(moduleId: string): string {
  return `forum-${moduleId}`;
}

// ─── Forum (module) ────────────────────────────────────────────────────────────

/**
 * Creates a FORUM and its spine module (kind='forum', ref_id=forum.id). The
 * forum uses a deterministic id per module — retries don't duplicate.
 */
export async function addForumModuleMutation(
  db: PowerSyncDrizzleDB,
  params: {
    courseId: string;
    unitId: string;
    title?: string;
    organizationId?: string | null;
    position?: number;
  },
): Promise<{ forumId: string; moduleId: string }> {
  const now = nowISO();
  // Module first (to derive a deterministic forum id per module).
  const moduleId = await addCourseModuleMutation(db, {
    courseId: params.courseId,
    unitId: params.unitId,
    kind: "forum",
    position: params.position ?? 0,
  });
  const forumId = forumIdForModule(moduleId);
  await db.insert(forum).values({
    id: forumId,
    module_id: moduleId,
    course_id: params.courseId,
    organization_id: params.organizationId ?? null,
    title: params.title?.trim() || "Foro",
    locked: 0,
    created_at: now,
    updated_at: now,
  });
  // Links the module to the forum (ref_id). The module was just created
  // (above, inside addCourseModuleMutation) — same mechanism as course→unit
  // from the canon: the view may not bring it yet if the queue drained in between.
  await patchRowVerified(db, course_module, course_module.id, moduleId, { ref_id: forumId });
  return { forumId, moduleId };
}

/**
 * Guarantees the `forum` row of an EXISTING module (deterministic id) without
 * creating a new module — for `forum` modules created by the generic flow.
 * The write-path applies the authz (course author/editor).
 *
 * ⚠️ The SELECT that decides whether the `forum` already exists lives inside
 * the window, but the id is DETERMINISTIC (`forumIdForModule`): a false "does
 * not exist" doesn't duplicate a different row, it retries the same id
 * (self-heals against the PK when the original syncs), unlike
 * `submitAssignmentMutation`. Not shielded with `waitForRowPresent` because
 * the same deterministic id could belong to a genuinely new `forum` module,
 * and waiting out the full backoff there would penalize the normal path.
 */
export async function ensureForumMutation(
  db: PowerSyncDrizzleDB,
  params: { moduleId: string; courseId: string; organizationId?: string | null; title?: string },
): Promise<string> {
  const forumId = forumIdForModule(params.moduleId);
  const now = nowISO();
  const existing = await db
    .select({ id: forum.id })
    .from(forum)
    .where(eq(forum.id, forumId))
    .limit(1);
  if (!existing[0]) {
    await db.insert(forum).values({
      id: forumId,
      module_id: params.moduleId,
      course_id: params.courseId,
      organization_id: params.organizationId ?? null,
      title: params.title?.trim() || "Foro",
      locked: 0,
      created_at: now,
      updated_at: now,
    });
    await patchRowVerified(db, course_module, course_module.id, params.moduleId, {
      ref_id: forumId,
    });
  }
  return forumId;
}

/**
 * ⚠️ No `scope` on purpose, even though `forum` DOES have `organization_id`
 * (2026-08-13 audit). `addForumModuleMutation`/`ensureForumMutation` (this
 * same file) create the forum from the AUTHORING flow (`course-editor.tsx`,
 * `addForumModuleMutation(d, { courseId, unitId, position })`) which NEVER
 * passes `organizationId` — a course is authored independent of organization
 * (it's shared across academies via cohorts) and `forum.organization_id`
 * stays NULL on that path. Wrapping it here with the ACTIVE org
 * (`useActiveOrg`) would make `eq(forum.organization_id, activeOrgId())`
 * NEVER match against a NULL column, and EVERY forum edit (title/lock) would
 * throw `LocalWriteLostError` — confirmed by reading the `insert(forum)`, not
 * just hypothetical. Closing this for real requires first populating
 * `organization_id` in the INSERT (out of scope for this change: it's a
 * completeness gap in the INSERT, not the UPDATE's `WHERE`). Signal that
 * would reopen it: the `forum` INSERT starts receiving a real `organizationId`.
 */
export async function updateForumMutation(
  db: PowerSyncDrizzleDB,
  forumId: string,
  updates: Partial<{ title: string; locked: boolean }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (updates.title !== undefined) {
    patch.title = updates.title;
  }
  if (updates.locked !== undefined) {
    patch.locked = updates.locked ? 1 : 0;
  }
  await patchRowVerified(db, forum, forum.id, forumId, patch, options);
}

// ─── Threads ────────────────────────────────────────────────────────────────────

export async function createThreadMutation(
  db: PowerSyncDrizzleDB,
  params: {
    forumId: string;
    cohortId: string | null;
    organizationId?: string | null;
    authorId: string;
    title: string;
    pinned?: boolean;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(forum_thread).values({
    id,
    forum_id: params.forumId,
    cohort_id: params.cohortId,
    organization_id: params.organizationId ?? null,
    author_id: params.authorId,
    title: params.title.trim() || "Nuevo hilo",
    pinned: params.pinned ? 1 : 0,
    locked: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * ⚠️ No `scope` on purpose (same reason as `updateForumMutation`, see its
 * note). `createThreadMutation` USUALLY does receive a real `organizationId`
 * (via the participant's cohort), but when the moderator is the course
 * AUTHOR without being a teacher of any cohort, `forum-module.tsx` falls
 * back to `forum.data?.organization_id` — which can be NULL (see the note
 * on `updateForumMutation`). Writing the `scope` there would break the
 * legitimate moderation of that edge case. It needs the source NULL
 * resolved first, not just threading the parameter through.
 */
export async function updateThreadMutation(
  db: PowerSyncDrizzleDB,
  threadId: string,
  updates: Partial<{ title: string; pinned: boolean; locked: boolean }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (updates.title !== undefined) {
    patch.title = updates.title;
  }
  if (updates.pinned !== undefined) {
    patch.pinned = updates.pinned ? 1 : 0;
  }
  if (updates.locked !== undefined) {
    patch.locked = updates.locked ? 1 : 0;
  }
  await patchRowVerified(db, forum_thread, forum_thread.id, threadId, patch, options);
}

/** ⚠️ Dead code: no callers in `apps/web` (no UI to delete a thread). */
export async function deleteThreadMutation(
  db: PowerSyncDrizzleDB,
  threadId: string,
): Promise<void> {
  const now = nowISO();
  await db
    .update(forum_thread)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(forum_thread.id, threadId));
}

// ─── Posts ────────────────────────────────────────────────────────────────────

export async function createPostMutation(
  db: PowerSyncDrizzleDB,
  params: {
    threadId: string;
    forumId: string;
    cohortId: string | null;
    organizationId?: string | null;
    authorId: string;
    body: string;
    replyToId?: string | null;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(forum_post).values({
    id,
    thread_id: params.threadId,
    forum_id: params.forumId,
    cohort_id: params.cohortId,
    organization_id: params.organizationId ?? null,
    author_id: params.authorId,
    body: params.body,
    reply_to_id: params.replyToId ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Edits the OWN body (the write-path only allows the author, and before hiding).
 * ⚠️ Dead code: no callers in `apps/web`.
 */
export async function updatePostBodyMutation(
  db: PowerSyncDrizzleDB,
  postId: string,
  body: string,
): Promise<void> {
  await db.update(forum_post).set({ body, updated_at: nowISO() }).where(eq(forum_post.id, postId));
}

/**
 * Hide/show a post (moderator). `hidden_by` is sealed by the server.
 * ⚠️ No `scope` on purpose — same reason as `updateThreadMutation`.
 */
export async function setPostHiddenMutation(
  db: PowerSyncDrizzleDB,
  postId: string,
  hidden: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    forum_post,
    forum_post.id,
    postId,
    { hidden_at: hidden ? nowISO() : null },
    options,
  );
}

/** ⚠️ Dead code: no callers in `apps/web`. */
export async function deletePostMutation(db: PowerSyncDrizzleDB, postId: string): Promise<void> {
  const now = nowISO();
  await db
    .update(forum_post)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(forum_post.id, postId));
}

// ─── Notifications (read_at only) ────────────────────────────────────────────

/**
 * ⚠️ No `scope` on purpose, even though `notification` DOES have `organization_id`
 * (isolation-gap audit, 2026-08-13). The bell aggregates notifications from
 * ALL of the user's organizations at once (`useMyNotifications`/
 * `useUnreadNotifications` filter only by `user_id`, with no `WHERE
 * organization_id`) — the ACTIVE org (`useActiveOrg`) is NOT the right
 * `scope` here: it would mark-as-not-landed the read of a legitimate
 * notification from an org that isn't the active one at that moment, a real
 * functional regression, not defense in depth. And using the `organization_id`
 * of the row itself would be circular (the row would be verified against
 * data read from itself) — zero protection. Closing this properly would
 * require resolving the org of EACH notification individually before
 * writing, not just threading a parameter through; it's left documented
 * instead of a `scope` that pretends to protect. Signal that would reopen it:
 * a `notificationId` belonging to someone else (a different user) landing
 * due to a miswired id — no known case today, `user_id` already bounds the read query.
 */
export async function markNotificationReadMutation(
  db: PowerSyncDrizzleDB,
  notificationId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    notification,
    notification.id,
    notificationId,
    { read_at: nowISO() },
    { ...options, hasUpdatedAt: false },
  );
}

/**
 * ⚠️ Excluded on purpose (POWERSYNC_VERIFIED_WRITE.md §4.4): the `WHERE` isn't
 * by PK, it's a batch `UPDATE` (all of a user's unread notifications). "0
 * rows" there is legitimate (there were none unread) and there isn't a
 * single id to reread in order to verify. Signal that would reopen it:
 * notifications a user already marked as read show up again as unread
 * after a refresh.
 */
export async function markAllNotificationsReadMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
): Promise<void> {
  await db
    .update(notification)
    .set({ read_at: nowISO() })
    .where(and(eq(notification.user_id, userId), isNull(notification.read_at)));
}

// ─── Announcements ─────────────────────────────────────────────────────────────────

export async function createAnnouncementMutation(
  db: PowerSyncDrizzleDB,
  params: {
    organizationId: string;
    campusId?: string | null;
    cohortId?: string | null;
    courseId?: string | null;
    authorId: string;
    title: string;
    body: string;
    pinned?: boolean;
    publishAt?: string | null;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(announcement).values({
    id,
    organization_id: params.organizationId,
    campus_id: params.campusId ?? null,
    cohort_id: params.cohortId ?? null,
    course_id: params.courseId ?? null,
    author_id: params.authorId,
    title: params.title.trim() || "Anuncio",
    body: params.body,
    pinned: params.pinned ? 1 : 0,
    publish_at: params.publishAt ?? now,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** ⚠️ Dead code: no callers in `apps/web`. */
export async function updateAnnouncementMutation(
  db: PowerSyncDrizzleDB,
  announcementId: string,
  updates: Partial<{ title: string; body: string; pinned: boolean; publishAt: string }>,
): Promise<void> {
  const patch: Record<string, unknown> = { updated_at: nowISO() };
  if (updates.title !== undefined) {
    patch.title = updates.title;
  }
  if (updates.body !== undefined) {
    patch.body = updates.body;
  }
  if (updates.pinned !== undefined) {
    patch.pinned = updates.pinned ? 1 : 0;
  }
  if (updates.publishAt !== undefined) {
    patch.publish_at = updates.publishAt;
  }
  await db.update(announcement).set(patch).where(eq(announcement.id, announcementId));
}

/** `organizationId`: the caller's ACTIVE org — see the `scope` note in `verified-write.ts`. */
export async function deleteAnnouncementMutation(
  db: PowerSyncDrizzleDB,
  announcementId: string,
  organizationId: string | undefined,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    announcement,
    announcement.id,
    announcementId,
    { deleted_at: nowISO() },
    withScope(options, [{ column: announcement.organization_id, value: organizationId }]),
  );
}
